import './claim-note-modal.scss';

import {
  AttachmentTargetType,
  AttachmentUploadInput,
  Attachments,
  DeleteNoteAttachment,
  NoteCategoryType,
  NoteTargetAreaType,
  NoteTargetType,
  useGetCategoriesForNote,
  useGetFolderList,
  useUpsertNoteMutation,
} from '@converge/graphql';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CreateClaimNoteField,
  getCreateClaimNoteFieldProps,
} from '@converge/data';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

// import AddIcon from '@mui/icons-material/Add';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import config from '@converge/config';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { DialogLayoutHr } from '../../../presentation/components/dialog-layout-hr';
import { FormTextField, NewTypeAhead } from '../../../presentation';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import { useConvergeSnackBar } from '../../../contexts';
import { FileUploadDropzone } from '../../../presentation/components/file-upload-dropzone';
import { IUploadFile } from '../../../presentation/components/file-uploader';

// Custom Note interface for the UI only - different from the GraphQL Note type
// Define attachment related types
export interface AttachmentNode {
  uuid: string;
  targetType?: AttachmentTargetType | string | null;
  targetName?: string | null;
  targetUuid?: string | null;
  dataSource?: string | null;
  filePath?: string | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  url?: string;
  folder: string;
}

export interface AttachmentEdge {
  node?: AttachmentNode | null;
}

export interface AttachmentEdges {
  edges?: Array<AttachmentEdge> | null;
}

export interface NoteUI {
  __typename?: string;
  uuid: string;
  note?: string | null;
  categories?: NoteCategoryType[] | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  targetUuid?: string | null;
  targetType?: NoteTargetType | null;
  targetName?: string | null;
  editable?: boolean | null;
  label?: string | null;
  attachments?: AttachmentEdges | null;
  user?: {
    node?: {
      uuid?: string | null;
      username?: string | null;
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  } | null;
}
interface ClaimNodeModalProps {
  open: boolean;
  onClose: () => void;
  onNoteSaved?: () => void;
  existingNote?: NoteUI; // Note being edited
}

interface CategoryOption {
  value: string;
  label: string;
  selected: boolean;
}

// Define a type that represents the form values
interface ClaimNoteFormValues extends FieldValues {
  [CreateClaimNoteField.Note]: string;
  [CreateClaimNoteField.Attachments]?: IUploadFile[];
  [CreateClaimNoteField.Categories]?: string[];
  [CreateClaimNoteField.Folder]?: string;
}

export const ClaimNoteModal: FC<ClaimNodeModalProps> = ({
  open,
  onClose,
  onNoteSaved,
  existingNote,
}) => {
  const formMethods = useForm<ClaimNoteFormValues>({
    defaultValues: {
      [CreateClaimNoteField.Note]: existingNote?.note || '',
      [CreateClaimNoteField.Categories]: existingNote?.categories || [],
      [CreateClaimNoteField.Folder]: '',
    },
  });

  const noteContent = formMethods.watch(CreateClaimNoteField.Note);

  const { categories: categoriesList } = useGetCategoriesForNote();
  const [showCategories, setShowCategories] = useState(true);
  const [showAttachments, setShowAttachments] = useState(true);
  const [showAttachedFiles, setShowAttachedFiles] = useState(true);
  const [selecttedFolder, setSelectedFolder] = useState('');
  // Initialize uploadedFiles based on whether we're editing an existing note with attachments
  const [uploadedFiles, setUploadedFiles] = useState<IUploadFile[]>(() => {
    if (existingNote?.attachments?.edges?.length) {
      // For edit mode: Convert attachment edges to IUploadFile objects with uuid, name, and url
      return existingNote.attachments.edges
        .filter((edge): edge is { node: AttachmentNode } => Boolean(edge?.node))
        .map((edge) => {
          const node = edge.node;
          return {
            id: node.uuid,
            file: new File([], node.filePath?.split('/').pop() || 'note'), // Create a placeholder File object
            folder: node.folder,
            url: node.url || '',
          } as IUploadFile;
        });
    }
    // For create mode: Return empty array
    return [];
  });
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const { data } = useGetFolderList();
  const folderList =
    data?.uploadFolders?.edges?.map((folder) => ({
      label: folder.node.label || folder.node.name,
      value: folder.node.name,
    })) || [];

  useEffect(() => {
    if (categoriesList && categoriesList.length > 0) {
      // If we're editing an existing note, mark the categories as selected
      if (existingNote && existingNote.categories) {
        const existingCategories = existingNote.categories;
        setCategories(
          (categoriesList as CategoryOption[]).map((category) => ({
            ...category,
            selected: existingCategories.includes(
              category.value as NoteCategoryType
            ),
          }))
        );
      } else {
        setCategories(categoriesList as CategoryOption[]);
      }
    }
  }, [categoriesList, existingNote]);
  const extractClaimUuidFromUrl = (
    url: string = window.location.pathname
  ): string => {
    const match = url.match(/\/claims\/([^/]+)/);
    return match ? match[1] : '';
  };

  const { notify } = useConvergeSnackBar();
  const { upsertNote, loading } = useUpsertNoteMutation({});
  const { DeleteAttachment } = DeleteNoteAttachment();
  const toggleCategory = useCallback((id: string) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) =>
        category.value === id
          ? { ...category, selected: !category.selected }
          : category
      )
    );
  }, []);

  const handleFilesAdded = useCallback((newFiles: IUploadFile[]) => {
    setUploadedFiles((prev) => [...prev, ...newFiles]);

    const intervals: NodeJS.Timeout[] = [];
    newFiles.forEach((file) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        if (progress > 100) {
          clearInterval(interval);
        } else {
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === file.id ? { ...f, progress } : f))
          );
        }
      }, 300);
      intervals.push(interval);
    });
  }, []);

  const handleAutoExpandAttachedFiles = useCallback(() => {
    if (!showAttachedFiles) {
      setShowAttachedFiles(true);
    }
  }, [showAttachedFiles]);

  const handleRemoveFile = useCallback(
    async (id: string) => {
      setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
      if (id && !id.includes('new-attachment')) {
        let noteResponseErrors;
        try {
          const { errors } = await DeleteAttachment({
            variables: {
              attachmentUuid: id,
            },
          });
          noteResponseErrors = errors;

          notify(`File has been deleted`, {
            variant: 'success',
          });
        } catch (cause) {
          console.error(`Failed to delete file. Please try again. (${cause})`);
          if (cause instanceof ApolloError) {
            noteResponseErrors = cause.graphQLErrors;
          }
        }
        const errorMessage = noteResponseErrors?.[0]?.message;
        if (errorMessage) {
          notify(
            `Failed to delete file. Please try again: (${
              errorMessage || 'No data returned by the API'
            })`,
            {
              variant: 'error',
            }
          );
          return;
        }
      }
    },
    [DeleteAttachment, notify]
  );

  // Function to reset form state
  const resetForm = useCallback(() => {
    // Reset form fields
    formMethods.reset({
      [CreateClaimNoteField.Note]: '',
      [CreateClaimNoteField.Categories]: [],
      [CreateClaimNoteField.Folder]: '',
    });

    // Reset categories
    setCategories((prevCategories) =>
      prevCategories.map((category) => ({ ...category, selected: false }))
    );

    // Reset selected folder dropdown
    setSelectedFolder('');

    // Reset uploaded files - keep existing attachments if editing
    if (!existingNote) {
      setUploadedFiles([]);
    } else {
      // Filter out any newly added files, keeping only original attachments
      setUploadedFiles((prev) => prev.filter((file) => file.url !== undefined));
    }
  }, [formMethods, existingNote]);

  const SubmitClaimNoteHandler = useCallback(
    (): SubmitHandler<ClaimNoteFormValues> => async (fields) => {
      const categoriesList = categories
        .filter((category) => category.selected === true)
        .map((cat) => cat.value);

      // Prepare attachments in the format expected by the API
      // Only include files without a url property (new uploads, not existing attachments)
      const newUploadedFiles = uploadedFiles.filter((file) => !file.url);

      const oversizedFiles = newUploadedFiles.filter(
        (f) => f.file.size > config.maxFileSizeInBytes
      );
      if (oversizedFiles.length > 0) {
        notify(
          `File too large: ${oversizedFiles[0].file.name}. Maximum size is ${config.maxFileSizeInMegabytes} MB.`,
          { variant: 'error' }
        );
        return;
      }

      const attachmentsData =
        newUploadedFiles.length > 0
          ? {
              attachments: {
                files: newUploadedFiles
                  .filter((attachment) => !attachment.url)
                  .map(
                    (file) =>
                      ({
                        file: file.file,
                        folder: file.folder,
                      } as AttachmentUploadInput)
                  ),
              } as Attachments,
            }
          : {};

      const noteData = {
        notes: fields.note,
        categories: categoriesList as NoteCategoryType[],
        targetArea: 'CLAIM' as NoteTargetAreaType,
        targetUuid: extractClaimUuidFromUrl(),
        ...(existingNote ? { noteUuid: existingNote.uuid } : {}),
        ...attachmentsData,
      };

      let noteResponseErrors: readonly GraphQLError[] | undefined;
      try {
        const { errors } = await upsertNote({
          variables: {
            details: noteData,
          },
        });
        noteResponseErrors = errors;

        // Reset form after successful submission
        resetForm();

        notify(`Note has been ${existingNote ? 'updated' : 'added'}`, {
          variant: 'success',
        });

        // Trigger refetch callback if provided
        if (onNoteSaved) {
          onNoteSaved();
        }

        onClose();
      } catch (cause) {
        console.error(`Failed to save Note. Please try again. (${cause})`);
        if (cause instanceof ApolloError) {
          noteResponseErrors = cause.graphQLErrors;
        }
        if (!noteResponseErrors?.length) {
          notify(`Failed to save note. Please try again.`, {
            variant: 'error',
          });
          return;
        }
      }
      const errorMessage = noteResponseErrors?.[0]?.message;
      if (errorMessage) {
        notify(
          `Failed to save note. Please try again: (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [
      categories,
      notify,
      onClose,
      onNoteSaved,
      resetForm,
      upsertNote,
      existingNote,
      uploadedFiles,
    ]
  );

  // Use useEffect to check button state whenever categories, note content, files, or folder changes
  useEffect(() => {
    // Store original values for edit mode comparison
    const originalNoteContent = existingNote?.note || '';
    const originalCategories =
      existingNote?.categories?.map((cat) => cat.toString()) || [];
    const originalFileCount = existingNote?.attachments?.edges?.length || 0;

    // Check if any categories are selected
    const hasSelectedCategories = categories.some(
      (category) => category.selected
    );

    // Check if note has content
    const hasNoteContent = noteContent?.length > 0;

    // Check if there are any uploaded files
    const hasUploadedFiles = uploadedFiles.length > 0;

    // Check if folder is selected
    const hasFolderSelected = selecttedFolder && selecttedFolder.trim() !== '';

    let isDisabled;

    // In edit mode, check if anything has actually changed
    if (existingNote) {
      const currentSelectedCategories = categories
        .filter((cat) => cat.selected)
        .map((cat) => cat.value);

      const noteChanged = noteContent !== originalNoteContent;
      const categoriesChanged =
        currentSelectedCategories.length !== originalCategories.length ||
        !currentSelectedCategories.every((cat) =>
          originalCategories.includes(cat)
        );

      const currentFileCount = uploadedFiles.length;
      const filesChanged = currentFileCount !== originalFileCount;

      const hasNewFiles = uploadedFiles.some((file) => !file.url);

      const folderSelected = hasFolderSelected;

      const hasChanges =
        noteChanged ||
        categoriesChanged ||
        filesChanged ||
        hasNewFiles ||
        folderSelected;

      if (!hasChanges) {
        // Nothing changed in edit mode: always disable
        isDisabled = true;
      } else if (!hasUploadedFiles && !hasFolderSelected) {
        // Neither file nor folder selected: only require note + category
        isDisabled = !(hasSelectedCategories && hasNoteContent);
      } else if (hasUploadedFiles && hasFolderSelected) {
        // Both file and folder selected: require all four conditions
        isDisabled = !(
          hasSelectedCategories &&
          hasNoteContent &&
          hasUploadedFiles &&
          hasFolderSelected
        );
      } else if (hasUploadedFiles && !hasFolderSelected) {
        // Files exist but no folder selected
        const hasExistingAttachments = uploadedFiles.some((file) => file.url);
        const hasNewUploads = uploadedFiles.some((file) => !file.url);

        if (hasExistingAttachments && !hasNewUploads) {
          // Only existing attachments, no new uploads: enable if note/categories valid
          isDisabled = !(hasSelectedCategories && hasNoteContent);
        } else {
          // Has new uploads without folder: always disable
          isDisabled = true;
        }
      } else {
        // Only folder selected but no files: always disable button
        isDisabled = true;
      }
    } else {
      // Create mode logic
      if (!hasUploadedFiles && !hasFolderSelected) {
        // Neither file nor folder selected: only require note + category
        isDisabled = !(hasSelectedCategories && hasNoteContent);
      } else if (hasUploadedFiles && hasFolderSelected) {
        // Both file and folder selected: require all four conditions
        isDisabled = !(
          hasSelectedCategories &&
          hasNoteContent &&
          hasUploadedFiles &&
          hasFolderSelected
        );
      } else {
        // Only one of file or folder selected: always disable button
        isDisabled = true;
      }
    }

    setIsSaveButtonDisabled(isDisabled);
  }, [categories, noteContent, uploadedFiles, selecttedFolder, existingNote]);
  const normalizeFolder = (folder: string) =>
    folder.toUpperCase().replace(/\s+/g, '_');
  function formatClaimKey(key: string) {
    if (key === 'CLAIM_FORMS') {
      return 'Claim Forms'; // or return "CLAIM_FORM" if you want it untouched
    }
    const cleaned = key.replace(/^CLAIM_/, '');

    // Replace underscores with spaces, lowercase all, then capitalize each word
    return cleaned
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        onClose();
      }}
      className="right-side-modal"
      maxWidth="md"
      fullScreen
    >
      <FormProvider {...formMethods}>
        <DialogTitle sx={{ padding: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            paddingTop="10px"
            paddingRight="15px"
          >
            <Typography></Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => {
                resetForm();
                onClose();
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogLayoutHr />
        <DialogContent
          sx={{ padding: 2 }}
          className="claim-node-modal-container"
        >
          {/* Rich Text Editor */}
          <Box sx={{ margin: '0px 50px 10px 50px' }}>
            <Box className="claim-note-title">
              {existingNote ? 'Edit Note' : 'Create Note'}
            </Box>
            <Box className="note-richtext-container">
              <FormTextField
                {...getCreateClaimNoteFieldProps(CreateClaimNoteField.Note)}
                containerWidth={12}
                placeholder="Add your note here"
                isRichText
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                py: 1,
              }}
              className="claim-note-description"
            >
              This entry will be editable for the next 24 hours only.
            </Typography>
          </Box>

          {/* Categories Section */}
          <Box sx={{ margin: '30px 50px 10px 50px' }}>
            <Box
              onClick={() => setShowCategories(!showCategories)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
              }}
            >
              <Box className="claim-title">
                {showCategories ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )}
                ADD CATEGORIES
              </Box>
            </Box>

            {showCategories && (
              <>
                <Box
                  sx={{ margin: '0px 20px 10px 24px' }}
                  className="claim-description"
                >
                  Select at least one category for this note.
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    margin: '15px 20px 10px 16px',
                  }}
                >
                  {categories.map((category) => (
                    <Chip
                      key={category.value}
                      label={category.label}
                      onClick={() => toggleCategory(category.value)}
                      variant={category.selected ? 'filled' : 'outlined'}
                    />
                  ))}
                  {/* temparary remove  */}
                  {/* <Box
                    className="add-category-button-container"
                    onClick={() => {
                    }}
                  >
                    <Box className="add-category-button">
                      <AddIcon className="add-icon" />
                    </Box>
                    <Box className="red-line"></Box>
                    <Box className="blue-dashed-line"></Box>
                  </Box> */}
                </Box>
              </>
            )}
          </Box>

          {/* Attachments Section */}
          <Box sx={{ margin: '30px 50px 10px 50px' }}>
            <Box
              onClick={() => setShowAttachments(!showAttachments)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
              }}
            >
              <Box className="claim-title">
                {showAttachments ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )}
                ADD ATTACHMENTS
              </Box>
            </Box>

            {showAttachments && (
              <>
                <Box
                  sx={{ margin: '0px 20px 20px 24px' }}
                  className="claim-description"
                >
                  First, select the folder for these documents.
                </Box>
                <Box
                  sx={{ margin: '0px 20px 60px 24px', width: '280px' }}
                  className="claim-description"
                >
                  <NewTypeAhead
                    options={folderList || []}
                    dropdownOptionWidth={true}
                    onChangeOption={(e) => setSelectedFolder(e?.value || '')}
                    placeholder="Select Folder"
                    {...getCreateClaimNoteFieldProps(
                      CreateClaimNoteField.Folder
                    )}
                    dataTestId="folder-note"
                  />
                </Box>
                <Box
                  sx={{ margin: '0px 20px 20px 24px' }}
                  className="claim-description"
                >
                  Then, select the documents.
                </Box>
                <Box sx={{ margin: '0px 20px 20px 24px' }}>
                  <FileUploadDropzone
                    selectedFolder={selecttedFolder}
                    disabled={selecttedFolder.length === 0}
                    onFilesAdded={handleFilesAdded}
                    onAutoExpand={handleAutoExpandAttachedFiles}
                    existingFiles={uploadedFiles.map((f) => ({
                      name: f.file.name,
                      folder: normalizeFolder(
                        f.url ? `CLAIM_${f.folder}` : f.folder
                      ),
                    }))}
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Attached Files Section */}

          <Box sx={{ margin: '30px 50px 10px 50px' }}>
            <Box
              onClick={() => setShowAttachedFiles(!showAttachedFiles)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
              }}
            >
              <Box className="claim-title">
                {showAttachedFiles ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )}
                Attached Files
              </Box>
            </Box>

            {showAttachedFiles && (
              <Box sx={{ p: 2 }}>
                {uploadedFiles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No files attached yet.
                  </Typography>
                ) : (
                  <Box>
                    {uploadedFiles.map((uploadedFile) => (
                      <Box
                        key={uploadedFile.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          py: 1,
                        }}
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{
                            marginRight: '4px',
                            color: '#000',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleRemoveFile(uploadedFile.id)}
                        />
                        <Box className="upload-name">
                          {
                            uploadedFile.url
                              ? `${uploadedFile.folder} / ${
                                  uploadedFile.file.name || uploadedFile.id
                                }` // For existing attachments
                              : `${formatClaimKey(uploadedFile.folder)} / ${
                                  uploadedFile.file.name
                                }` // For newly uploaded files
                          }
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Save Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginRight: '200px',
              width: '93%',
              marginBottom: '30px',
            }}
          >
            <Box style={{ width: existingNote ? '140px' : '120px' }}>
              <NewConvergeButton
                loading={loading}
                variant="contained"
                label={`${existingNote ? 'UPDATE' : 'SAVE'} NOTE`}
                onClick={formMethods.handleSubmit(SubmitClaimNoteHandler())}
                disabled={isSaveButtonDisabled}
                sx={{
                  bgcolor: '#8dd0c3',
                  '&:hover': {
                    bgcolor: '#7bc0b3',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#cccccc',
                    color: '#666666',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
